import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import SmallResourcePreview from 'components/Utils/SmallResourcePreview';
import PhysicianUtils from 'services/PhysicianUtils';
import CalendarUtils from 'services/CalendarUtils';
import _ from 'lodash';
import './style.scss';
import PerformanceUtils from 'services/PerformanceUtils';
import Icon from 'components/Utils/NewIcon';
import Graph from 'components/HealthRecords/Graph';
import DefaultModal from 'components/Utils/DefaultModal';
import DocumentList from 'components/HealthRecords/Documents/DocumentList';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import { OTHER_HEALTH_RECORDS } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/constants';
import { GetLatestMetricValueOriginEnum } from 'generated/api';
import CPPUtils from 'services/CPPUtils';
import Linkify from 'linkify-react';

const AppointmentNoteDetails = (props) => (
  <div className="details-panel padded-box scroll-list appointment-note-details">
    <NoteContent {...props} />
  </div>
);

export const NoteContent = (props) =>
  isText(props.note) ? (
    <TextAppointmentNoteElement {...props} />
  ) : (
    <>
      <AttachmentNoteContent {...props} />
      {props.items}
      {props.hoverIcons}
    </>
  );

export const AttachmentNoteContent = (props) => {
  const dispatch = useAppDispatch();

  function getFile(dispatchOptions) {
    return dispatch(
      api.HealthRecords.getNotesAttachment({
        appointmentNoteId: props.note.id,
      }),
      dispatchOptions,
    );
  }

  return (
    <div className="appointment-note">
      <NoteInfoText {...props} />
      <SmallResourcePreview
        patient={props.patient}
        title="Appointment Note"
        url={props.note.value.value}
        getFile={getFile}
      />
      <NoteDocumentList documents={props.documents} />
    </div>
  );
};

export const TextAppointmentNoteElement = React.memo(
  ({ note, date, showInfoText = true, documents, items, hoverIcons }) => {
    const summary =
      useAppSelector((state) => state.healthRecords.byId[note.patient_id]?.dataSummary) ||
      DEFAULT_ARRAY;

    const { index_patient, value } = note.value;

    let hidden = index_patient > 0 ? value.substring(0, index_patient) : undefined;
    let shown = index_patient > 0 ? value.substring(index_patient).trim() : value;

    let className = 'appointment-note is-new-format';

    const numberGTPALValues = useMemo(() => {
      return CPPUtils.getNumberOfGTPALValues(note.health_records?.['gtpal_main']);
    }, [note.health_records]);

    const noteDate = note.value.date;

    const relevantMetrics = useMemo(() => {
      return getTodaysRelevantMetrics(summary, noteDate);
    }, [summary, noteDate]);

    const showItemsSection = useMemo(() => {
      return (
        hasHealthRecords(note, numberGTPALValues) ||
        documents?.length > 0 ||
        items?.length > 0 ||
        note.value.metrics?.length > 0 ||
        relevantMetrics.length > 0
      );
    }, [note, items, documents, relevantMetrics, numberGTPALValues]);

    return (
      <div key={'note-' + date || note.value.date} className={className}>
        <NoteInfoText note={note} date={date} showInfoText={showInfoText} />
        <div className="row note-columns-wrapper gap-[5px]">
          <pre className="note-section">
            <Linkify options={{ target: '_blank', rel: 'noopener noreferrer' }}>
              <div>{hoverIcons}</div>
              <div className="hidden-text">{hidden}</div>
              <div>{shown}</div>
            </Linkify>
          </pre>
          {showItemsSection && (
            <div className="note-section note-section-items">
              <div className="floater">
                <HealthRecords note={note} />
                <Metrics note={note} additionalMetrics={relevantMetrics} />
                <NoteDocumentList documents={documents} />
                {items}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
);

const NoteDocumentList = ({ documents }) => {
  const dispatch = useAppDispatch();

  function onPin() {
    _.reverse(documents).forEach((document) => {
      dispatch(
        uiActions.addPinnedDocument({
          type: 'document',
          id: document.id,
          patientId: document.patient_id,
        }),
      );
    });
  }

  return (
    !!documents && (
      <>
        <div className="document-section-title-wrapper">
          <span className="small-title my-4">Documents</span>
          <DefaultButton
            style={{ width: '100%' }}
            name="Pin All To Side"
            icon="thumbtack"
            onClick={onPin}
          />
        </div>
        <DocumentList documents={documents} allowPin={true} />
      </>
    )
  );
};

function hasHealthRecords(note, GTPALValues = 0) {
  const values = OTHER_HEALTH_RECORDS.map((hr) => [hr, note.health_records?.[hr.id] || []]);
  return !!values.reduce((acc, next) => acc + next[1].length, 0) || GTPALValues > 0;
}

const HealthRecords = React.memo(({ note }) => {
  const values = OTHER_HEALTH_RECORDS.map((hr) => [hr, note.health_records?.[hr.id] || []]);

  const numberGTPALValues = useMemo(() => {
    return CPPUtils.getNumberOfGTPALValues(note.health_records?.['gtpal_main']);
  }, [note.health_records]);

  const hasValues = hasHealthRecords(note, numberGTPALValues);

  return (
    hasValues && (
      <div className="health-records">
        {values.map(([hr, values]) => {
          let isGTPAL = hr.id === 'gtpal';
          let hasGTPALValues = isGTPAL && (values.length > 0 || numberGTPALValues > 0);

          return (
            <React.Fragment key={hr.id}>
              {isGTPAL && hasGTPALValues && (
                <div className="record">
                  <span className="record-title">{hr.name}</span>
                </div>
              )}
              {hr?.id === 'gtpal' &&
                hasGTPALValues &&
                CPPUtils.getGTPAL(note.health_records?.['gtpal_main'])}
              {values.length > 0 && (
                <HealthRecordValues hr={hr} values={values} hideTitle={isGTPAL} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    )
  );
});

const HealthRecordValues = React.memo(({ hr, values, hideTitle = false }) => {
  if (hr.id === 'history') return <HistoryValues hr={hr} values={values} />;

  function getDescription(hr, value) {
    return hr.fields
      .filter((field) => (hr.id !== 'history' ? field !== 'history_type' : true))
      .map((field) => (value.value || {})[field])
      .filter((item) => !!item)
      .join(', ');
  }

  return (
    <div className="record">
      {!hideTitle && <span className="record-title">{hr.name}</span>}
      {values.map((value, index) => (
        <React.Fragment key={index}>
          <span className="record-value">{getDescription(hr, value)}</span>
        </React.Fragment>
      ))}
    </div>
  );
});

const HistoryValues = React.memo(({ hr, values }) => {
  values = PerformanceUtils.sortBy(values, ['value.history_type']);

  function getDescription(hr, value) {
    return hr.fields
      .filter((field) => field !== 'history_type')
      .map((field) => (value.value || {})[field])
      .filter((item) => !!item)
      .join(', ');
  }

  function historyChanged(values, index) {
    if (index === 0) return true;
    return values[index].value.history_type !== values[index - 1].value.history_type;
  }

  return (
    <div className="record">
      {values.map((value, index) => (
        <React.Fragment key={index}>
          {historyChanged(values, index) && (
            <span className="record-title">{value.value.history_type}</span>
          )}
          <span className="record-value">{getDescription(hr, value)}</span>
        </React.Fragment>
      ))}
    </div>
  );
});

const DEFAULT_ARRAY = [];

const METRIC_STYLE = { marginRight: '5px' };
const Metrics = React.memo(({ note, additionalMetrics = DEFAULT_ARRAY }) => {
  const patient = useAppSelector((state) => state.patients.byId[note.patient_id]);
  const metricTypes = useAppSelector((state) => state.healthRecords.metricTypes);

  const [graphMetric, setGraphMetric] = useState(undefined);

  const metrics = useMemo(() => {
    let metricsLeftToAdd = [...additionalMetrics];

    let result = [];

    (note.value.metrics || []).forEach((metric) => {
      let { name } = metric;
      let type;

      if (name.includes('/')) {
        let split = name.split('/');
        name = split[0];
        type = split[1];
      }

      let matchingType = metricTypes.find((type) => type.id === name);
      if (matchingType) name = matchingType.name;
      else {
        name = PerformanceUtils.capitalizeFirstLetter(name);
      }

      metricsLeftToAdd = metricsLeftToAdd.filter((val) => val.metric.id !== metric.name);

      result.push(
        <MetricValue
          key={'metric-' + name}
          name={name}
          setGraphMetric={setGraphMetric}
          value={metric.value}
          type={type}
        />,
      );
    });

    metricsLeftToAdd.forEach((metric) => {
      let value = metric.last_value.value1;
      if (metric.last_value.value2) value += `/${metric.last_value.value2}`;
      result.push(
        <MetricValue
          name={metric.metric.name}
          setGraphMetric={setGraphMetric}
          value={value}
          type={metric.metric.type}
        />,
      );
    });

    return result;
  }, [note, metricTypes, additionalMetrics]);

  return (
    metrics.length > 0 && (
      <div className="metrics">
        {!!graphMetric && (
          <DefaultModal
            className="graph-modal large-modal"
            title="Metrics"
            content={<Graph patient={patient} defaultMetric={graphMetric} />}
            isOpen={!!graphMetric}
            onRequestClose={() => setGraphMetric()}
            wrap={true}
            padding={false}
          />
        )}
        <span className="metrics-title">Metrics</span>
        {metrics}
      </div>
    )
  );
});

const MetricValue = ({ name, type, value, setGraphMetric }) => {
  return (
    <span className="metric" style={METRIC_STYLE} onClick={() => setGraphMetric(name)}>
      <Icon src="chart-line" width="15px" />
      {name}
      {type ? ` (${type})` : ''}: {value}
    </span>
  );
};

function getTodaysRelevantMetrics(summary = [], noteDate) {
  const relevantIds = ['height', 'weight', 'bmi'];
  const origin = GetLatestMetricValueOriginEnum.Practitioner;
  let result = summary.filter((item) => {
    return (
      item.origin === origin &&
      relevantIds.includes(item.metric.id) &&
      CalendarUtils.isSameDay(moment.unix(item.last_value.date), moment.unix(noteDate))
    );
  });
  return result;
}

const NoteInfoText = React.memo(({ note, showInfoText, date }) => {
  let infoText = [];

  const physicians = useAppSelector((state) => state.physicians.byId);

  if (note.history && note.history.length > 1) infoText.push(note.history.length - 1 + ' edits');

  if (note.value.hide === true) infoText.push('hidden');

  let authors = (note.history || []).map((history) => history.author_id);

  authors = _.reverse(_.uniq([...authors, note.author_id]));
  authors = authors
    .filter((author) => !!author)
    .map((authorId) => PhysicianUtils.getName(physicians[authorId]))
    .join(' / ');

  let documents = note.documents || [];

  return (
    showInfoText === true && (
      <>
        <span className="author">{authors}</span>
        <span className="date">
          {CalendarUtils.format(date || note.value.date, 'DD/MM/YY h:mm A')}
        </span>
        {infoText.length > 0 && (
          <span className="info-text">
            ({infoText.join(', ')})
            {documents.length > 0 && (
              <>
                <Icon fontAwesome="paperclip" height="15px" width="15px" />
                {documents.length}
              </>
            )}
          </span>
        )}
      </>
    )
  );
});

function isText(note) {
  return note.item_type === 'text';
}

AppointmentNoteDetails.propTypes = {
  note: PropTypes.object.isRequired,
  patient: PropTypes.object.isRequired,
};

export default AppointmentNoteDetails;
